import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Building2, MapPin, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { OnboardingState } from '@/hooks/useOnboardingState';
import { LOCATION_DATA } from '@/lib/locationData';

interface OrganizationCreationFormProps {
  state: OnboardingState;
  updateState: (updates: Partial<OnboardingState>) => void;
}

export const OrganizationCreationForm = ({
  state,
  updateState
}: OrganizationCreationFormProps) => {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <Building2 className="mx-auto h-12 w-12 text-primary mb-4" />
        <h3 className="text-xl font-semibold mb-2">Neues Team erstellen</h3>
        <p className="text-muted-foreground">
          Legen Sie den Namen und Standort Ihres neuen Teams fest.
        </p>
      </div>

      <div className="space-y-4">
        {/* Organization Name */}
        <div className="space-y-2">
          <Label htmlFor="orgName">Team-Name</Label>
          <div className="relative">
            <Input
              id="orgName"
              value={state.organizationName}
              onChange={(e) => updateState({ organizationName: e.target.value })}
              placeholder="z.B. Mein Team München"
              className={`pr-10 ${
                state.isNameTaken ? 'border-red-500 focus:border-red-500' : 
                state.organizationName && !state.isCheckingName && !state.isNameTaken ? 'border-green-500' : ''
              }`}
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {state.isCheckingName && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              )}
              {!state.isCheckingName && state.organizationName && state.isNameTaken && (
                <AlertCircle className="h-4 w-4 text-red-500" />
              )}
              {!state.isCheckingName && state.organizationName && !state.isNameTaken && state.organizationName.length >= 3 && (
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              )}
            </div>
          </div>
          {state.isNameTaken && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              Dieser Name ist bereits vergeben
            </p>
          )}
          {!state.isCheckingName && state.organizationName && !state.isNameTaken && state.organizationName.length >= 3 && (
            <p className="text-sm text-green-600 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Name verfügbar
            </p>
          )}
        </div>

        {/* Organization Color */}
        <div className="space-y-2">
          <Label htmlFor="orgColor">Team-Farbe</Label>
          <div className="flex items-center gap-3">
            <input
              id="orgColor"
              type="color"
              value={state.organizationColor}
              onChange={(e) => updateState({ organizationColor: e.target.value })}
              className="w-12 h-10 rounded border border-border cursor-pointer"
            />
            <Input
              value={state.organizationColor}
              onChange={(e) => updateState({ organizationColor: e.target.value })}
              placeholder="#3B82F6"
              className="flex-1"
            />
          </div>
        </div>

        {/* Location Selection */}
        <Card className="border-2 border-dashed">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-5 w-5 text-primary" />
              <h4 className="font-medium">Standort auswählen</h4>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Country Selection */}
              <div className="space-y-2">
                <Label>Land</Label>
                <Select
                  value={state.selectedCountry}
                  onValueChange={(value) => updateState({ 
                    selectedCountry: value, 
                    selectedState: '', 
                    selectedCity: '' 
                  })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Land wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(LOCATION_DATA).map((country) => (
                      <SelectItem key={country} value={country}>
                        {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* State Selection */}
              <div className="space-y-2">
                <Label>Bundesland/Region</Label>
                <Select
                  value={state.selectedState}
                  onValueChange={(value) => updateState({ 
                    selectedState: value, 
                    selectedCity: '' 
                  })}
                  disabled={!state.selectedCountry}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Bundesland wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {state.selectedCountry && Object.keys(LOCATION_DATA[state.selectedCountry] || {}).map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* City Selection */}
              <div className="space-y-2">
                <Label>Stadt</Label>
                <Select
                  value={state.selectedCity}
                  onValueChange={(value) => updateState({ selectedCity: value })}
                  disabled={!state.selectedState}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Stadt wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {state.selectedState && (LOCATION_DATA[state.selectedCountry]?.[state.selectedState] || []).map((city) => (
                      <SelectItem key={city} value={city}>
                        {city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Generated Team Name Preview */}
            {state.generatedTeamName && (
              <div className="mt-4 p-3 bg-muted rounded-lg">
                <div className="text-sm font-medium text-muted-foreground mb-1">
                  Generierter Team-Name:
                </div>
                <div className="text-lg font-semibold text-primary">
                  {state.generatedTeamName}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};